/**
 * CourseForm — create/edit a Course.
 * Uses react-hook-form + zodResolver.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

const CEFR_LEVELS = [
  { value: 'pre_a1', label: 'Pre A1' },
  { value: 'a1', label: 'A1' },
  { value: 'a2', label: 'A2' },
  { value: 'b1', label: 'B1' },
  { value: 'b2', label: 'B2' },
  { value: 'c1', label: 'C1' },
  { value: 'c2', label: 'C2' },
] as const;

const courseFormSchema = z
  .object({
    code: z.string().min(1, 'Bắt buộc').max(50),
    name: z.string().min(1, 'Bắt buộc').max(200),
    level: z.enum(['pre_a1', 'a1', 'a2', 'b1', 'b2', 'c1', 'c2']),
    description: z.string().optional().or(z.literal('')),
    totalSessions: z.coerce.number().int().positive('Phải ≥ 1'),
    sessionDurationMin: z.coerce.number().int().positive('Phải ≥ 1'),
    targetAgeMin: z.coerce.number().int().nonnegative().optional().or(z.literal('')),
    targetAgeMax: z.coerce.number().int().nonnegative().optional().or(z.literal('')),
    isActive: z.boolean(),
    entryCriteria: z.string().optional().or(z.literal('')),
    exitCriteria: z.string().optional().or(z.literal('')),
  })
  .refine(
    (d) => {
      if (d.targetAgeMin !== '' && d.targetAgeMax !== '' &&
          d.targetAgeMin !== undefined && d.targetAgeMax !== undefined) {
        return Number(d.targetAgeMin) <= Number(d.targetAgeMax);
      }
      return true;
    },
    { message: 'Tuổi tối thiểu phải ≤ tuổi tối đa', path: ['targetAgeMin'] },
  );

export type CourseFormValues = z.infer<typeof courseFormSchema>;

interface CourseFormProps {
  defaultValues?: Partial<CourseFormValues>;
  isEdit?: boolean;
  isSubmitting?: boolean;
  hideCancel?: boolean;
  onSubmit: (values: CourseFormValues) => void;
  onCancel: () => void;
}

export function CourseForm({
  defaultValues,
  isEdit,
  isSubmitting,
  hideCancel,
  onSubmit,
  onCancel,
}: CourseFormProps) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<CourseFormValues>({
    resolver: zodResolver(courseFormSchema),
    defaultValues: {
      level: 'a1',
      sessionDurationMin: 90,
      isActive: true,
      entryCriteria: '',
      exitCriteria: '',
      ...defaultValues,
    },
  });

  useEffect(() => {
    reset({
      level: 'a1',
      sessionDurationMin: 90,
      isActive: true,
      entryCriteria: '',
      exitCriteria: '',
      ...defaultValues,
    });
  }, [defaultValues, reset]);

  const level = watch('level');
  const isActive = watch('isActive');

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
      {/* Code — read-only when editing */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="c-code" className="text-sm font-medium">
          Mã khóa <span className="text-destructive">*</span>
        </Label>
        <Input
          id="c-code"
          className="h-10 font-mono"
          placeholder="STARTERS-A1"
          disabled={isEdit}
          {...register('code')}
        />
        {errors.code && <p className="text-xs text-destructive">{errors.code.message}</p>}
      </div>

      {/* Name */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="c-name" className="text-sm font-medium">
          Tên khóa học <span className="text-destructive">*</span>
        </Label>
        <Input id="c-name" className="h-10" placeholder="Starters A1" {...register('name')} />
        {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
      </div>

      {/* Level + isActive */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Trình độ CEFR <span className="text-destructive">*</span>
          </Label>
          <Select value={level} onValueChange={(v) => setValue('level', v as CourseFormValues['level'])}>
            <SelectTrigger className="h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CEFR_LEVELS.map((l) => (
                <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.level && <p className="text-xs text-destructive">{errors.level.message}</p>}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">Còn mở khóa mới</Label>
          <div className="flex items-center h-10 gap-3">
            <Switch
              checked={isActive}
              onCheckedChange={(v) => setValue('isActive', v)}
            />
            <span className="text-sm text-muted-foreground">
              {isActive ? 'Đang mở' : 'Ngừng'}
            </span>
          </div>
        </div>
      </div>

      {/* Sessions + Duration */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="totalSessions" className="text-sm font-medium">
            Tổng buổi <span className="text-destructive">*</span>
          </Label>
          <Input id="totalSessions" type="number" min={1} className="h-10" {...register('totalSessions')} />
          {errors.totalSessions && <p className="text-xs text-destructive">{errors.totalSessions.message}</p>}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="sessionDurationMin" className="text-sm font-medium">
            Thời lượng (phút) <span className="text-destructive">*</span>
          </Label>
          <Input id="sessionDurationMin" type="number" min={1} className="h-10" {...register('sessionDurationMin')} />
          {errors.sessionDurationMin && <p className="text-xs text-destructive">{errors.sessionDurationMin.message}</p>}
        </div>
      </div>

      {/* Target age */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="targetAgeMin" className="text-sm font-medium">Tuổi tối thiểu</Label>
          <Input id="targetAgeMin" type="number" min={0} className="h-10" placeholder="Tùy chọn" {...register('targetAgeMin')} />
          {errors.targetAgeMin && <p className="text-xs text-destructive">{errors.targetAgeMin.message}</p>}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="targetAgeMax" className="text-sm font-medium">Tuổi tối đa</Label>
          <Input id="targetAgeMax" type="number" min={0} className="h-10" placeholder="Tùy chọn" {...register('targetAgeMax')} />
          {errors.targetAgeMax && <p className="text-xs text-destructive">{errors.targetAgeMax.message}</p>}
        </div>
      </div>

      {/* Description */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="description" className="text-sm font-medium">Mô tả</Label>
        <Textarea id="description" rows={3} placeholder="Mô tả chương trình..." {...register('description')} />
      </div>

      {/* Entry / Exit criteria */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="entryCriteria" className="text-sm font-medium">Điều kiện đầu vào</Label>
        <Textarea
          id="entryCriteria"
          rows={2}
          placeholder="Yêu cầu học viên trước khi vào khóa học..."
          {...register('entryCriteria')}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="exitCriteria" className="text-sm font-medium">Tiêu chí đầu ra</Label>
        <Textarea
          id="exitCriteria"
          rows={2}
          placeholder="Kết quả học viên đạt được sau khóa học..."
          {...register('exitCriteria')}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
        {!hideCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
            Hủy
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {isEdit ? 'Đang lưu…' : 'Đang tạo…'}
            </span>
          ) : isEdit ? 'Lưu thay đổi' : 'Tạo khóa học'}
        </Button>
      </div>
    </form>
  );
}
